/**
 * src/components/Financial/SettingsSection.jsx
 * 【完全版：アコーディオンUI・銘柄追加&観測地統合・ダークモード入力アイコン修正済】
 */
import React, { useState, useEffect } from "react";
import {
  RefreshCw,
  Palette,
  Layers,
  Settings,
  LayoutGrid,
  ChevronDown,
  ChevronUp,
  MapPin,
  Plus,
  Trash2,
  Globe,
} from "lucide-react";
import { PLANET_SYMBOLS, PLANET_NAMES_JP } from "../../utils/constants.js";

// =============================================================================
// 内部定数
// =============================================================================
// --- 修正後：タイムゾーン情報を含めた都市プリセット ---
const CITIES = [
  { name: "東京", lat: 35.6895, lon: 139.6917, tz: 9 },
  { name: "大阪", lat: 34.6937, lon: 135.5023, tz: 9 },
  { name: "ニューヨーク", lat: 40.7128, lon: -74.006, tz: -5 },
  { name: "ロンドン", lat: 51.5074, lon: -0.1278, tz: 0 },
  { name: "シドニー", lat: -33.8688, lon: 151.2093, tz: 11 },
];

// =============================================================================
// 1. 内部コンポーネント: AccordionItem
// =============================================================================
const AccordionItem = function (props) {
  const isOpen = props.isOpen;
  const onToggle = props.onToggle;
  const title = props.title;
  const children = props.children;
  const isDarkMode = props.isDarkMode;

  return (
    <div style={{ marginBottom: "10px" }}>
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          background: isDarkMode ? "#2d2c33" : "#f8fafc",
          borderRadius: isOpen ? "12px 12px 0 0" : "12px",
          border: isDarkMode
            ? "1px solid rgba(255,255,255,0.05)"
            : "1px solid #e2e8f0",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
      >
        <span
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: isDarkMode ? "#fff" : "#1e293b",
          }}
        >
          {title}
        </span>
        {isOpen ? (
          <ChevronUp size={18} color={isDarkMode ? "#888" : "#64748b"} />
        ) : (
          <ChevronDown size={18} color={isDarkMode ? "#888" : "#64748b"} />
        )}
      </div>
      {isOpen && (
        <div
          style={{
            padding: "20px",
            background: isDarkMode ? "rgba(255, 255, 255, 0.02)" : "#fff",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.05)"
              : "1px solid #e2e8f0",
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// 2. メインコンポーネント: SettingsSection
// =============================================================================
export default function SettingsSection(props) {
  const isDarkMode = props.isDarkMode;
  const planetColors = props.planetColors;
  const setPlanetColors = props.setPlanetColors;
  const signColors = props.signColors;
  const setSignColors = props.setSignColors;
  const onReset = props.onReset;
  const aspectSettings = props.aspectSettings;
  const onAspectSettingsChange = props.onAspectSettingsChange;
  const horoscopeStyle = props.horoscopeStyle;
  const onHoroscopeStyleChange = props.onHoroscopeStyleChange;
  const hideAspectLines = props.hideAspectLines;
  const onHideAspectLinesChange = props.onHideAspectLinesChange;

  const assets = props.assets || [];
  const onAddCustom = props.onAddCustom;
  const onDeleteCustom = props.onDeleteCustom;

  const observerLat = props.observerLat;
  const setObserverLat = props.setObserverLat;
  const observerLon = props.observerLon;
  const setObserverLon = props.setObserverLon;
  const isCustomLocation = props.isCustomLocation;
  const setIsCustomLocation = props.setIsCustomLocation;

  const [openSection, setOpenSection] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  const [newAsset, setNewAsset] = useState({
    name: "",
    category: "仮想通貨",
    date: "",
    time: "12:00",
    timezone: 9,
    description: "",
  });

  useEffect(function () {
    const handleResize = function () {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleSection = function (section) {
    setOpenSection(openSection === section ? null : section);
  };

  const handleAddAsset = function () {
    if (!newAsset.name || !newAsset.date) {
      alert("銘柄名と日付は必須です");
      return;
    }
    const id = "custom_" + Date.now();
    onAddCustom(Object.assign({}, newAsset, { id: id, isCustom: true }));
    setNewAsset({
      name: "",
      category: "仮想通貨",
      date: "",
      time: "12:00",
      timezone: 9,
      description: "",
    });
    alert("銘柄を追加しました");
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #ddd",
    background: isDarkMode ? "#2d2c33" : "#fff",
    color: isDarkMode ? "#fff" : "#1e293b",
    fontSize: "14px",
    outline: "none",
    // 【重要】これによって、input内のカレンダー/時計アイコンがダークモード時に白くなります
    colorScheme: isDarkMode ? "dark" : "light",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#9ca3af" : "#64748b",
    display: "block",
    marginBottom: "8px",
  };

  return (
    <div style={{ padding: isMobile ? "0px" : "5px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
        }}
      >
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "900",
            color: isDarkMode ? "#fff" : "#2c2d96",
            margin: 0,
          }}
        >
          デザイン・計算設定
        </h2>
        <button
          onClick={onReset}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            background: isDarkMode ? "#2d2c33" : "#fff",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid #e2e8f0",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: "bold",
            color: "#ef4444",
            cursor: "pointer",
          }}
        >
          <RefreshCw size={14} /> デフォルト設定に戻す
        </button>
      </div>

      <AccordionItem
        title="銘柄を追加"
        isOpen={openSection === "asset"}
        onToggle={() => toggleSection("asset")}
        isDarkMode={isDarkMode}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "15px",
            }}
          >
            <div>
              <label style={labelStyle}>銘柄名 *</label>
              <input
                type="text"
                placeholder="例: Bitcoin"
                value={newAsset.name}
                onChange={(e) =>
                  setNewAsset(
                    Object.assign({}, newAsset, { name: e.target.value }),
                  )
                }
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>カテゴリー</label>
              <select
                value={newAsset.category}
                onChange={(e) =>
                  setNewAsset(
                    Object.assign({}, newAsset, { category: e.target.value }),
                  )
                }
                style={inputStyle}
              >
                <option value="米国株">米国株</option>
                <option value="仮想通貨">仮想通貨</option>
                <option value="株価指数">株価指数</option>
                <option value="個別株">個別株</option>
                <option value="為替">為替</option>
                <option value="コモディティ">コモディティ</option>
                <option value="その他(出生日)">その他(出生日)</option>
              </select>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
              gap: "15px",
            }}
          >
            <div>
              <label style={labelStyle}>日付 *</label>
              <input
                type="date"
                value={newAsset.date}
                onChange={(e) =>
                  setNewAsset(
                    Object.assign({}, newAsset, { date: e.target.value }),
                  )
                }
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>時刻</label>
              <input
                type="time"
                value={newAsset.time}
                onChange={(e) =>
                  setNewAsset(
                    Object.assign({}, newAsset, { time: e.target.value }),
                  )
                }
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>タイムゾーン (UTC)</label>
              <select
                value={newAsset.timezone}
                onChange={(e) =>
                  setNewAsset(
                    Object.assign({}, newAsset, {
                      timezone: Number(e.target.value),
                    }),
                  )
                }
                style={inputStyle}
              >
                {Array.from({ length: 25 }, (_, i) => i - 12).map((tz) => (
                  <option key={tz} value={tz}>
                    UTC{tz >= 0 ? "+" : ""}{tz}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: "10px", color: isDarkMode ? "#888" : "#94a3b8", marginTop: "4px" }}>
                ※サマータイム期間中の場合は数値を +1 調整してください
              </p>
            </div>
          </div>
          <button
            onClick={handleAddAsset}
            style={{
              background: "linear-gradient(45deg, #2c2d96 0%, #9c2393 100%)",
              color: "#fff",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              fontWeight: "bold",
              cursor: "pointer",
              marginTop: "10px",
            }}
          >
            <Plus
              size={16}
              style={{ marginRight: "8px", verticalAlign: "middle" }}
            />{" "}
            銘柄を追加
          </button>

          {assets.filter((a) => a.isCustom).length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <label style={labelStyle}>登録済みカスタム銘柄</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {assets
                  .filter((a) => a.isCustom)
                  .map((asset) => (
                    <div
                      key={asset.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 12px",
                        background: isDarkMode ? "#2d2c33" : "#f8fafc",
                        borderRadius: "8px",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid #e2e8f0",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          color: isDarkMode ? "#fff" : "#1e293b",
                        }}
                      >
                        {asset.name}
                      </span>
                      <Trash2
                        size={14}
                        color="#ef4444"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (confirm("削除しますか？"))
                            onDeleteCustom(asset.id);
                        }}
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </AccordionItem>

      <AccordionItem
        title="観測地"
        isOpen={openSection === "location"}
        onToggle={() => toggleSection("location")}
        isDarkMode={isDarkMode}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={labelStyle}>プリセットから選択</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {CITIES.map((city) => (
                <button
                  key={city.name}
                  onClick={() => {
                    setIsCustomLocation(false);
                    setObserverLat(city.lat);
                    setObserverLon(city.lon);
                    // タイムゾーンも連動して更新する
                    if (props.setTransitTz) props.setTransitTz(city.tz);
                    console.log(
                      "--- [DEBUG: Settings] 都市切り替え ---",
                      city.name,
                      "TZ:",
                      city.tz,
                    );
                  }}
                  style={{
                    padding: "10px 15px",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor:
                      !isCustomLocation && observerLat === city.lat
                        ? "#2c2d96"
                        : isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : "#e2e8f0",
                    background:
                      !isCustomLocation && observerLat === city.lat
                        ? "#2c2d96"
                        : isDarkMode
                          ? "#2d2c33"
                          : "#fff",
                    color:
                      !isCustomLocation && observerLat === city.lat
                        ? "#fff"
                        : isDarkMode
                          ? "#fff"
                          : "#64748b",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <MapPin size={14} style={{ marginRight: "6px" }} />{" "}
                  {city.name}
                </button>
              ))}
              <button
                onClick={() => setIsCustomLocation(true)}
                style={{
                  padding: "10px 15px",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: isCustomLocation
                    ? "#2c2d96"
                    : isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "#e2e8f0",
                  background: isCustomLocation
                    ? "#2c2d96"
                    : isDarkMode
                      ? "#2d2c33"
                      : "#fff",
                  color: isCustomLocation
                    ? "#fff"
                    : isDarkMode
                      ? "#fff"
                      : "#64748b",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                カスタム座標
              </button>
            </div>
          </div>
          {isCustomLocation && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
              }}
            >
              <div>
                <label style={labelStyle}>緯度</label>
                <input
                  type="number"
                  value={observerLat}
                  onChange={(e) => setObserverLat(Number(e.target.value))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>経度</label>
                <input
                  type="number"
                  value={observerLon}
                  onChange={(e) => setObserverLon(Number(e.target.value))}
                  style={inputStyle}
                />
              </div>
            </div>
          )}
        </div>
      </AccordionItem>

      {/* ハウス計算設定セクション：画像 f301.jpg の選択機能を実現 */}
      <AccordionItem
        title="ハウス・計算設定"
        isOpen={openSection === "calculation"}
        onToggle={() => toggleSection("calculation")}
        isDarkMode={isDarkMode}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={labelStyle}>
              ハウスシステム (ジオセントリック時)
            </label>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => props.setHouseSystem("placidus")}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor:
                    props.houseSystem === "placidus" ? "#2c2d96" : "#ddd",
                  background:
                    props.houseSystem === "placidus"
                      ? "#2c2d96"
                      : isDarkMode
                        ? "#2d2c33"
                        : "#fff",
                  color:
                    props.houseSystem === "placidus"
                      ? "#fff"
                      : isDarkMode
                        ? "#ccc"
                        : "#64748b",
                  fontSize: "12px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                プラシーダス (標準)
              </button>
              <button
                onClick={() => props.setHouseSystem("equal")}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor:
                    props.houseSystem === "equal" ? "#2c2d96" : "#ddd",
                  background:
                    props.houseSystem === "equal"
                      ? "#2c2d96"
                      : isDarkMode
                        ? "#2d2c33"
                        : "#fff",
                  color:
                    props.houseSystem === "equal"
                      ? "#fff"
                      : isDarkMode
                        ? "#ccc"
                        : "#64748b",
                  fontSize: "12px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                等分ハウス (Equal)
              </button>
            </div>
          </div>
        </div>
      </AccordionItem>

      <AccordionItem
        title="ホロスコープ表示設定"
        isOpen={openSection === "horoscope"}
        onToggle={() => toggleSection("horoscope")}
        isDarkMode={isDarkMode}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={labelStyle}>表示スタイル</label>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "10px",
              }}
            >
              <button
                onClick={() => onHoroscopeStyleChange("style1")}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  background:
                    horoscopeStyle === "style1"
                      ? "#2c2d96"
                      : isDarkMode
                        ? "#2d2c33"
                        : "#fff",
                  color: horoscopeStyle === "style1" ? "#fff" : "#64748b",
                  fontSize: "12px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                スタイル1 (標準)
              </button>
              <button
                onClick={() => onHoroscopeStyleChange("style2")}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  background:
                    horoscopeStyle === "style2"
                      ? "#2c2d96"
                      : isDarkMode
                        ? "#2d2c33"
                        : "#fff",
                  color: horoscopeStyle === "style2" ? "#fff" : "#64748b",
                  fontSize: "12px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                スタイル2 (モノクロ・円なし)
              </button>
            </div>
          </div>
          <label
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: isDarkMode ? "#fff" : "#1e293b",
            }}
          >
            <input
              type="checkbox"
              checked={hideAspectLines}
              onChange={(e) => onHideAspectLinesChange(e.target.checked)}
              style={{ width: "18px", height: "18px" }}
            />
            <span>アスペクトラインを非表示にする</span>
          </label>
        </div>
      </AccordionItem>

      <AccordionItem
        title="天体・感受点のカラー"
        isOpen={openSection === "planet_color"}
        onToggle={() => toggleSection("planet_color")}
        isDarkMode={isDarkMode}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: "12px",
          }}
        >
          {/* null ガードを追加してクラッシュを防止 */}
          {Object.keys(planetColors || {}).length > 0 ? (
            Object.keys(planetColors || {})
              .filter(function (id) {
                const excludeIds = [
                  "Ceres",
                  "Pallas",
                  "Juno",
                  "Vesta",
                  "Hygeia",
                  "Industria",
                  "Psyche",
                  "Abundantia",
                  "Sedna",
                  "Pholus",
                  "Lilith",
                  "Chiron",
                  "Fortuna",
                  "Eris",
                  "Midas",
                  "Damocles",
                ];
                return !excludeIds.includes(id);
              })
              .map((id) => (
                <div
                  key={id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: isDarkMode ? "#2d2c33" : "#fff",
                    padding: "10px",
                    borderRadius: "8px",
                    border: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                  }}
                >
                  <input
                    type="color"
                    value={planetColors[id]}
                    onChange={(e) => {
                      const nc = Object.assign({}, planetColors);
                      nc[id] = e.target.value;
                      setPlanetColors(nc);
                    }}
                    style={{
                      width: "28px",
                      height: "28px",
                      padding: "0",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: "none",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: isDarkMode ? "#fff" : "#334155",
                      }}
                    >
                      {PLANET_SYMBOLS[id]} {PLANET_NAMES_JP[id] || id}
                    </span>
                    <span
                      style={{
                        fontSize: "9px",
                        color: isDarkMode ? "#888" : "#94a3b8",
                      }}
                    >
                      {id}
                    </span>
                  </div>
                </div>
              ))
          ) : (
            <div style={{ color: theme.subText, fontSize: "12px" }}>
              設定データを読み込めません
            </div>
          )}
        </div>
      </AccordionItem>

      <AccordionItem
        title="星座(サイン)のカラー"
        isOpen={openSection === "sign_color"}
        onToggle={() => toggleSection("sign_color")}
        isDarkMode={isDarkMode}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
            gap: "12px",
          }}
        >
          {Object.keys(signColors).map((sym) => (
            <div
              key={sym}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: isDarkMode ? "#2d2c33" : "#fff",
                padding: "10px",
                borderRadius: "12px",
                border: isDarkMode
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid #e2e8f0",
              }}
            >
              <input
                type="color"
                value={signColors[sym]}
                onChange={(e) => {
                  const nc = Object.assign({}, signColors);
                  nc[sym] = e.target.value;
                  setSignColors(nc);
                }}
                style={{
                  width: "28px",
                  height: "28px",
                  padding: "0",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  background: "none",
                }}
              />
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: isDarkMode ? "#fff" : "#1e293b",
                }}
              >
                {sym + "\uFE0E"}
              </span>
            </div>
          ))}
        </div>
      </AccordionItem>

      <AccordionItem
        title="アスペクト表示・検索設定 (Orb)"
        isOpen={openSection === "orb"}
        onToggle={() => toggleSection("orb")}
        isDarkMode={isDarkMode}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <label style={labelStyle}>主要アスペクト最大Orb</label>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "900",
                  color: isDarkMode ? "#fff" : "#2c2d96",
                }}
              >
                {aspectSettings.majorOrb.toFixed(1)}°
              </span>
            </div>
            <input
              type="range"
              min="0.1"
              max="10"
              step="0.1"
              value={aspectSettings.majorOrb}
              onChange={(e) =>
                onAspectSettingsChange(
                  Object.assign({}, aspectSettings, {
                    majorOrb: parseFloat(e.target.value),
                  }),
                )
              }
              style={{ width: "100%", cursor: "pointer" }}
            />
          </div>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <label style={labelStyle}>マイナーアスペクト最大Orb</label>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "900",
                  color: isDarkMode ? "#fff" : "#2c2d96",
                }}
              >
                {aspectSettings.minorOrb.toFixed(1)}°
              </span>
            </div>
            <input
              type="range"
              min="0.1"
              max="5"
              step="0.1"
              value={aspectSettings.minorOrb}
              onChange={(e) =>
                onAspectSettingsChange(
                  Object.assign({}, aspectSettings, {
                    minorOrb: parseFloat(e.target.value),
                  }),
                )
              }
              style={{ width: "100%", cursor: "pointer" }}
            />
          </div>
          <div
            style={{
              background: isDarkMode ? "#2d2c33" : "#f8fafc",
              padding: "15px",
              borderRadius: "12px",
              border: isDarkMode
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid #e2e8f0",
            }}
          >
            <label
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                color: isDarkMode ? "#fff" : "#1e293b",
              }}
            >
              <input
                type="checkbox"
                checked={aspectSettings.useOuterLimit}
                onChange={(e) =>
                  onAspectSettingsChange(
                    Object.assign({}, aspectSettings, {
                      useOuterLimit: e.target.checked,
                    }),
                  )
                }
              />
              <span>外惑星同士(木〜冥)はタイトなOrbを適用する</span>
            </label>
            {aspectSettings.useOuterLimit && (
              <div
                style={{
                  marginTop: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={labelStyle}>制限値:</span>
                <input
                  type="number"
                  value={aspectSettings.outerPlanetOrb}
                  onChange={(e) =>
                    onAspectSettingsChange(
                      Object.assign({}, aspectSettings, {
                        outerPlanetOrb: parseFloat(e.target.value),
                      }),
                    )
                  }
                  style={Object.assign({}, inputStyle, { width: "80px" })}
                />
              </div>
            )}
          </div>
        </div>
      </AccordionItem>
    </div>
  );
}
